import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay. */
const MODAL_BREAKPOINT = 990;

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px), the drawer uses a non-modal dialog and
 * squeezes page content.
 *
 * On narrow viewports (< 990px), the drawer uses a native modal dialog,
 * backdrop, native modal semantics, and root scroll locking.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing drawer stacking counter. */
  static #stackOrder = 0;

  /** Prevents duplicate asynchronous close operations. */
  #isClosing = false;

  /** Drawer open request received while closing. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element that should regain focus when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** Mobile modal breakpoint. */
  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(
    `(max-width: ${MODAL_BREAKPOINT - 1}px)`
  );

  /**
   * Whether the custom drawer state is currently open.
   *
   * @returns {boolean}
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    const { panel } = this.refs;

    this.#modalQuery.addEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    panel.addEventListener('cancel', this.#onNativeCancel);
    panel.addEventListener('close', this.#onNativeClose);

    // Synchronize the static counter with state restored before this
    // module was loaded.
    const restoredOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restores listeners and scroll locking for a drawer restored by the
   * synchronous state-restoration script.
   */
  #onRestore() {
    const { panel } = this.refs;

    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { panel } = this.refs;

    panel.removeEventListener('cancel', this.#onNativeCancel);
    panel.removeEventListener('close', this.#onNativeClose);

    this.#modalQuery.removeEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    unlockScroll(panel);

    this.#removeEventListeners();
    removeTrapFocus();

    this.#deferredOpen = false;
    this.#isClosing = false;
    this.#previouslyFocused = null;
  }

  /**
   * Handles native dialog cancellation, including supported mobile
   * browser Back gestures/buttons.
   *
   * @param {Event} event
   */
  #onNativeCancel = (event) => {
    if (!this.#modalQuery.matches || !this.isOpen) return;

    event.preventDefault();
    void this.close();
  };

  /**
   * Handles a native close that occurs outside the controlled close path.
   */
  #onNativeClose = () => {
    const { panel } = this.refs;

    // A breakpoint change or drawer restack can close and immediately
    // reopen the same native dialog. Ignore its stale close event.
    if (panel.open) return;

    // Always make the shared cleanup safe if the native dialog is closed.
    unlockScroll(panel);

    // The controlled close path owns the remaining cleanup.
    if (this.#isClosing) return;

    // The custom state is already synchronized.
    if (!this.isOpen) return;

    this.#finalizeExternallyClosedDrawer();
  };

  /**
   * Synchronizes custom state after an external native dialog dismissal.
   */
  #finalizeExternallyClosedDrawer() {
    const { panel } = this.refs;

    this.#removeEventListeners();
    removeTrapFocus();
    unlockScroll(panel);

    this.removeAttribute('open');
    this.style.removeProperty('--drawer-stack-order');

    this.#deferredOpen = false;
    this.#isClosing = false;

    this.dispatchEvent(new DrawerCloseEvent());

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    this.#restoreFocus();
  }

  /**
   * Closes when the user clicks the backdrop.
   *
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      void this.close();
    }
  };

  /**
   * Whether the drawer contains another open native dialog.
   *
   * @returns {boolean}
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * Returns an open dialog nested inside the drawer panel.
   *
   * @returns {HTMLDialogElement | null}
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (
      this.refs.panel.querySelector('dialog[open]')
    );
  }

  /**
   * Switches between mobile modal and desktop non-modal modes.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();

    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the old dialog mode and immediately reopen in the new mode.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(
        nestedDialog,
        nestedActiveElement
      );
    } else {
      this.#focusInitialElement();
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Handles Escape through the controlled close path.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    void this.close();
  };

  /**
   * Opens the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }

    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    if (this.#modalQuery.matches) {
      lockScroll(panel);

      try {
        panel.showModal();
      } catch (error) {
        unlockScroll(panel);
        panel.classList.remove(openClass);
        this.style.removeProperty('--drawer-stack-order');

        console.error('Unable to open drawer dialog.', error);
        return;
      }
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    void onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      { subtree: false }
    );

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Brings an already-open drawer to the front.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    // Native modal stacking follows showModal() order.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    void onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      { subtree: false }
    );

    this.dispatchEvent(new DrawerOpenEvent());
  }

  /**
   * Removes drawer interaction listeners.
   */
  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the drawer close button.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector(
        '.theme-drawer__close-button'
      )
    );

    closeButton?.focus();
  }

  /**
   * Restores focus to the element that opened the drawer.
   */
  #restoreFocus() {
    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    if (trigger && document.contains(trigger)) {
      trigger.focus();
      return;
    }

    const fallbackTrigger = /** @type {HTMLElement | null} */ (
      document.querySelector(`[aria-controls="${this.id}"]`)
    );

    fallbackTrigger?.focus();
  }

  /**
   * Reopens a nested modal so it stays above the drawer.
   *
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement | null} focusTarget
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (
      focusTarget &&
      document.contains(focusTarget) &&
      dialog.contains(focusTarget)
    ) {
      focusTarget.focus();
    }
  }

  /**
   * Runs the controlled drawer close sequence.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;

    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    // Page-interaction cleanup must not depend on animation completion.
    unlockScroll(panel);

    if (panel.open) {
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start'
      );

      panel.classList.add('theme-drawer__dialog--closing');

      await Promise.race([
        onAnimationEnd(
          panel,
          undefined,
          { subtree: false }
        ),
        new Promise((resolve) => {
          window.setTimeout(resolve, 500);
        }),
      ]);

      panel.classList.remove(
        'theme-drawer__dialog--closing'
      );
    }

    if (panel.open) {
      panel.close();
    }

    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    this.#restoreFocus();

    this.#isClosing = false;

    // Reconcile an open request received during the close animation.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      this.#deferredOpen = false;
      return;
    }

    await this.#closeDialog();
  }

  /**
   * Toggles the drawer.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }

    return this.open();
  }

  /**
   * Whether this drawer is the highest open drawer in the stack.
   *
   * @returns {boolean}
   */
  #isTopmost() {
    const myOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    for (
      const other of document.querySelectorAll(
        'theme-drawer[open]'
      )
    ) {
      if (other === this) continue;

      const otherOrder = Number(
        /** @type {HTMLElement} */ (
          other
        ).style.getPropertyValue('--drawer-stack-order') || 0
      );

      if (otherOrder > myOrder) return false;
    }

    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, {
      bubbles: true,
    });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, {
      bubbles: true,
    });
  }

  static eventName = 'theme-drawer:close';
}